/**
 * The frame side of `phoneVerifyFrame.ts`: runs on `app.<domain>/phone-verify` inside a company page,
 * sends and checks the code with the web Firebase SDK, and answers its parent by `postMessage`.
 *
 * It answers only `window.parent`, only when the parent's origin is this domain or one of its
 * subdomains, and posts every reply to that exact origin. A page loaded directly (not in a frame)
 * does nothing.
 */

import {
  isTagged,
  isTrustedParentOrigin,
  PHONE_VERIFY_TAG,
  type FromFrame,
  type ToFrame,
  type Untagged,
} from './phoneVerifyFrame.js';
import {
  toVerificationError,
  type PhoneCodeSession,
  type PhoneVerifier,
} from './phoneVerification.js';

export interface PhoneVerifyFrameHostOptions {
  /** The web verifier, created on first use (Firebase is imported only when someone sends a code). */
  createVerifier(): Promise<PhoneVerifier>;
  win?: Window;
}

/** Starts answering the parent; returns the function that stops it. */
export function servePhoneVerification({
  createVerifier,
  win = window,
}: PhoneVerifyFrameHostOptions): () => void {
  const parent = win.parent;
  if (!parent || parent === win) return () => undefined;

  let verifier: Promise<PhoneVerifier> | null = null;
  const sessions = new Map<number, PhoneCodeSession>();
  let nextSession = 1;
  /** The parent's origin, once it has asked for something; replies go only there. */
  let askedBy: string | null = null;

  function reply(origin: string, message: Untagged<FromFrame, 'tag'>) {
    parent.postMessage({ ...message, tag: PHONE_VERIFY_TAG } as FromFrame, origin);
  }

  async function handle(message: ToFrame, origin: string) {
    try {
      if (message.kind === 'send') {
        verifier ??= createVerifier();
        const session = await (await verifier).send(message.phone);
        const id = nextSession++;
        sessions.set(id, session);
        reply(origin, { kind: 'sent', id: message.id, session: id });
      } else {
        const session = sessions.get(message.session);
        if (!session) {
          reply(origin, { kind: 'failed', id: message.id, reason: 'code_expired' });
          return;
        }
        const proof = await session.confirm(message.code);
        sessions.delete(message.session);
        reply(origin, { kind: 'proof', id: message.id, proof });
      }
    } catch (error) {
      if (message.kind === 'send') {
        // Start the next send fresh (a failed config fetch or SDK load is not cached).
        const failed = verifier;
        verifier = null;
        void failed?.then((created) => created.dispose()).catch(() => undefined);
      }
      reply(origin, { kind: 'failed', id: message.id, reason: toVerificationError(error).reason });
    }
  }

  function onMessage(event: MessageEvent) {
    if (event.source !== parent || !isTrustedParentOrigin(event.origin, win.location)) return;
    if (!isTagged<ToFrame>(event.data)) return;
    const message = event.data;
    if ((message.kind === 'send' || message.kind === 'confirm') && typeof message.id === 'number') {
      askedBy = event.origin;
      void handle(message, event.origin);
    }
  }
  win.addEventListener('message', onMessage);

  // reCAPTCHA shows its puzzle in a `bframe` iframe whose wrapper flips between hidden and visible.
  // The parent keeps this frame invisible until then, so the person can see and answer the puzzle.
  // `visibility` is inherited, so the puzzle iframe's own computed value says whether it shows.
  let open = false;
  const Observer = (win as Window & typeof globalThis).MutationObserver;
  const observer = new Observer(() => {
    if (!askedBy) return;
    const challenge = win.document.querySelector<HTMLIFrameElement>(
      'iframe[src*="recaptcha"][src*="bframe"]',
    );
    const visible = challenge ? win.getComputedStyle(challenge).visibility === 'visible' : false;
    if (visible !== open) {
      open = visible;
      reply(askedBy, { kind: 'challenge', open });
    }
  });
  observer.observe(win.document.body, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['style'],
  });

  // Nothing secret in it, and the parent's origin is not known yet: `*` is fine for this one.
  parent.postMessage({ tag: PHONE_VERIFY_TAG, kind: 'ready' } satisfies FromFrame, '*');

  return () => {
    win.removeEventListener('message', onMessage);
    observer.disconnect();
    sessions.clear();
    void verifier?.then((created) => created.dispose()).catch(() => undefined);
  };
}
